import { useState } from 'react';
import type { AttributeDef } from '@flexiplanner/types';
import { Check, Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import {
  normalizeAttributeOption,
  parseAttributeOptions,
  withAddedAttributeOption,
} from '@/lib/attributeOptions';

const ADD_OPTION_VALUE = '__flexiplanner_add_enum_option__';

interface EnumAttributeInputProps {
  attr: AttributeDef;
  value: unknown;
  onChange: (value: string) => void;
  onAttributeUpdated?: (attr: AttributeDef) => void;
  compact?: boolean;
  triggerId?: string;
}

export function EnumAttributeInput({
  attr,
  value,
  onChange,
  onAttributeUpdated,
  compact = false,
  triggerId,
}: EnumAttributeInputProps) {
  const [isAdding, setIsAdding] = useState(false);
  const [newOption, setNewOption] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const options = parseAttributeOptions(attr);

  async function saveNewOption() {
    const clean = normalizeAttributeOption(newOption);
    if (!clean) return;
    setIsSaving(true);
    setError(null);
    const nextAttr = withAddedAttributeOption(attr, clean);
    try {
      const response = await api.user.updateAttribute(attr.attr_id, {
        name: nextAttr.name,
        data_type: 'enum',
        specificity: nextAttr.specificity,
        field_weight: nextAttr.field_weight ?? 1,
        custom_math_map: nextAttr.custom_math_map || null,
        options: parseAttributeOptions(nextAttr).join(', '),
        min_val: nextAttr.min_val ?? null,
        max_val: nextAttr.max_val ?? null,
        tensor_index: nextAttr.tensor_index || 0,
      });
      if (!response.success) throw new Error(response.error?.message || 'Could not add option');
      onAttributeUpdated?.(nextAttr);
      onChange(clean);
      setNewOption('');
      setIsAdding(false);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Could not add option');
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className={cn('grid gap-1.5', compact && 'min-w-[150px]')}>
      <div className="flex items-center gap-1.5">
        <Select
          value={String(value ?? '')}
          onValueChange={nextValue => {
            if (nextValue === ADD_OPTION_VALUE) {
              setIsAdding(true);
              setError(null);
              return;
            }
            onChange(nextValue);
          }}
        >
          <SelectTrigger id={triggerId} className={cn(compact && 'h-8')}>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {options.map(option => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
            {options.length > 0 && <SelectSeparator />}
            <SelectItem value={ADD_OPTION_VALUE} className="text-primary">
              Add option...
            </SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={cn('shrink-0', compact ? 'h-8 w-8' : 'h-10 w-10')}
          onClick={() => {
            setIsAdding(value => !value);
            setError(null);
          }}
          title={`Add ${attr.name} option`}
          aria-label={`Add ${attr.name} option`}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {isAdding && (
        <div className="flex items-center gap-1.5">
          <Input
            value={newOption}
            onChange={event => setNewOption(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') {
                event.preventDefault();
                void saveNewOption();
              }
              if (event.key === 'Escape') {
                setIsAdding(false);
                setNewOption('');
                setError(null);
              }
            }}
            placeholder={`New ${attr.name.toLowerCase()}`}
            className={cn(compact && 'h-8')}
            autoFocus
          />
          <Button
            type="button"
            size="icon"
            className={cn('shrink-0', compact ? 'h-8 w-8' : 'h-10 w-10')}
            disabled={!normalizeAttributeOption(newOption) || isSaving}
            onClick={() => void saveNewOption()}
            aria-label={`Save ${attr.name} option`}
          >
            {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={cn('shrink-0', compact ? 'h-8 w-8' : 'h-10 w-10')}
            onClick={() => {
              setIsAdding(false);
              setNewOption('');
              setError(null);
            }}
            aria-label={`Cancel new ${attr.name} option`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
